import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Modal, Checkbox, message, Input, Space, App, Select } from 'antd';
import { DownloadOutlined, TeamOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import MainLayout from '../../components/Layout/MainLayout';
import { albaService } from '../../services/albaService';
import { AlbaExam, OnlineApplicant } from '../../types/alba';
import { validateKoreanID } from '../../utils/validators';
import api from '../../services/api';
import dayjs from 'dayjs';

const AlbaList: React.FC = () => {
  const { modal, message: messageApi } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [onlineExamList, setOnlineExamList] = useState<AlbaExam[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedExams, setSelectedExams] = useState<number[]>([]);
  const [applicantModalVisible, setApplicantModalVisible] = useState(false);
  const [applicants, setApplicants] = useState<OnlineApplicant[]>([]);
  const [selectedApplicants, setSelectedApplicants] = useState<number[]>([]);
  const [companyName, setCompanyName] = useState('');
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [statsModalVisible, setStatsModalVisible] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [statsStartDate, setStatsStartDate] = useState('');
  const [statsEndDate, setStatsEndDate] = useState('');
  const [poolModalVisible, setPoolModalVisible] = useState(false);
  const [poolList, setPoolList] = useState<any[]>([]);
  const [poolFormName, setPoolFormName] = useState('');
  const [poolFormJumin, setPoolFormJumin] = useState('');
  const [poolFormCell, setPoolFormCell] = useState('');
  const [poolSearchKeyword, setPoolSearchKeyword] = useState('');
  const [poolJuminError, setPoolJuminError] = useState('');
  const [albaJuminError, setAlbaJuminError] = useState('');
  const [poolCellError, setPoolCellError] = useState('');
  const [albaCellError, setAlbaCellError] = useState('');
  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [albaSearchModalVisible, setAlbaSearchModalVisible] = useState(false);
  const [searchDateEnabled, setSearchDateEnabled] = useState(true);
  const [searchStartDate, setSearchStartDate] = useState('');
  const [searchEndDate, setSearchEndDate] = useState('');
  const [searchJobName, setSearchJobName] = useState('');
  const [searchAlbaName, setSearchAlbaName] = useState('');
  const [jobNameList, setJobNameList] = useState<string[]>([]);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadData, setUploadData] = useState<any[]>([]);
  const [albaList, setAlbaList] = useState<any[]>([]);
  const [selectedAlbaKeys, setSelectedAlbaKeys] = useState<React.Key[]>([]);
  const [editAlbaModalVisible, setEditAlbaModalVisible] = useState(false);
  const [editAlbaData, setEditAlbaData] = useState<any>(null);

  // 일용직 입력 폼 상태
  const [jobName, setJobName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [albaName, setAlbaName] = useState('');
  const [albaJumin, setAlbaJumin] = useState('');
  const [albaCell, setAlbaCell] = useState('');
  const [albaCost, setAlbaCost] = useState('');

  // 주민번호 포맷팅 및 검증 (일용직)
  const handleAlbaJuminChange = (value: string) => {
    const formatted = formatJumin(value);
    setAlbaJumin(formatted);

    // 13자리 입력 완료 시 검증
    const numbers = formatted.replace(/[^0-9]/g, '');
    if (numbers.length === 13) {
      const validation = validateKoreanID(numbers);
      setAlbaJuminError(validation.valid ? '' : validation.reason || '');
    } else {
      setAlbaJuminError('');
    }
  };

  // 주민번호 포맷팅 및 검증 (인력풀)
  const handlePoolJuminChange = (value: string) => {
    const formatted = formatJumin(value);
    setPoolFormJumin(formatted);

    // 13자리 입력 완료 시 검증
    const numbers = formatted.replace(/[^0-9]/g, '');
    if (numbers.length === 13) {
      const validation = validateKoreanID(numbers);
      setPoolJuminError(validation.valid ? '' : validation.reason || '');
    } else {
      setPoolJuminError('');
    }
  };

  // 주민번호 포맷팅 (6자리-7자리)
  const formatJumin = (value: string) => {
    const numbers = value.replace(/[^0-9]/g, '');
    if (numbers.length <= 6) {
      return numbers;
    }
    return `${numbers.slice(0, 6)}-${numbers.slice(6, 13)}`;
  };

  // 전화번호 포맷팅 및 검증 (일용직)
  const handleAlbaCellChange = (value: string) => {
    const formatted = formatPhone(value);
    setAlbaCell(formatted);

    // 전화번호 자리수 체크 (최소 9자리)
    const numbers = formatted.replace(/[^0-9]/g, '');
    if (numbers.length > 0 && numbers.length < 9) {
      setAlbaCellError('전화번호는 최소 9자리 이상이어야 합니다.');
    } else if (numbers.length > 11) {
      setAlbaCellError('전화번호는 최대 11자리입니다.');
    } else {
      setAlbaCellError('');
    }
  };

  // 전화번호 포맷팅 및 검증 (인력풀)
  const handlePoolCellChange = (value: string) => {
    const formatted = formatPhone(value);
    setPoolFormCell(formatted);

    // 전화번호 자리수 체크 (최소 9자리)
    const numbers = formatted.replace(/[^0-9]/g, '');
    if (numbers.length > 0 && numbers.length < 9) {
      setPoolCellError('전화번호는 최소 9자리 이상이어야 합니다.');
    } else if (numbers.length > 11) {
      setPoolCellError('전화번호는 최대 11자리입니다.');
    } else {
      setPoolCellError('');
    }
  };

  // 전화번호 포맷팅 (3자리-4자리-4자리)
  const formatPhone = (value: string) => {
    const numbers = value.replace(/[^0-9]/g, '');
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    }
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  // 엑셀 파일 저장 (선택된 항목)
  const handleSaveExcel = async () => {
    if (albaList.length === 0) {
      message.warning('저장할 목록이 없습니다.');
      return;
    }

    try {
      // 선택된 항목 또는 전체
      const dataToExport = selectedAlbaKeys.length > 0
        ? albaList.filter(item => selectedAlbaKeys.includes(item.App_IDX))
        : albaList;

      if (dataToExport.length === 0) {
        message.warning('저장할 항목을 선택해주세요.');
        return;
      }

      // 백엔드 형식에 맞게 변환 (포맷팅 적용)
      const applicantsData = dataToExport.map((item, index) => ({
        jobName: item.Job_Name,
        eduDate: item.S_Date,
        examDate: item.E_Date,
        name: item.Alba_Name,
        jumin: formatJumin(item.Alba_Jumin),  // 포맷팅: 123456-1234567
        cell: formatPhone(item.Alba_Cell),     // 포맷팅: 010-1234-5678
        cost: item.Alba_Cost                   // 숫자 그대로 (ExcelJS가 천단위 콤마 처리)
      }));

      const blob = await albaService.downloadApplicantsExcel(applicantsData);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = `일용직_등록리스트_${new Date().toISOString().split('T')[0].replace(/-/g, '')}.xlsx`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('엑셀 파일이 다운로드되었습니다.');
    } catch (error) {
      console.error('엑셀 저장 오류:', error);
      message.error('엑셀 파일 저장 중 오류가 발생했습니다.');
    }
  };

  // 선택 삭제 (목록 + DB 반영)
  const handleDeleteSelectedAlba = () => {
    if (selectedAlbaKeys.length === 0) {
      message.warning('삭제할 항목을 선택해주세요.');
      return;
    }

    modal.confirm({
      title: '선택 삭제',
      content: `${selectedAlbaKeys.length}건을 삭제하시겠습니까?\n(DB에서도 삭제됩니다. 인력풀은 유지)`,
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          // DB에서 삭제 (tblApp)
          const response = await api.post('/api/alba/deleteApp', {
            appIds: selectedAlbaKeys
          });

          if (response.data.resultCode === 200) {
            // 목록에서 제거
            const filtered = albaList.filter(item => !selectedAlbaKeys.includes(item.App_IDX));
            setAlbaList(filtered);
            setSelectedAlbaKeys([]);
            message.success(response.data.message || '삭제되었습니다.');
          } else {
            message.error(response.data.message || '삭제 실패');
          }
        } catch (error) {
          console.error('삭제 오류:', error);
          message.error('삭제 중 오류가 발생했습니다.');
        }
      },
    });
  };

  // 선택 수정
  const handleEditSelectedAlba = () => {
    if (selectedAlbaKeys.length === 0) {
      message.warning('수정할 항목을 선택해주세요.');
      return;
    }
    if (selectedAlbaKeys.length > 1) {
      message.warning('수정은 한 건씩만 가능합니다.');
      return;
    }

    const item = albaList.find(a => a.App_IDX === selectedAlbaKeys[0]);
    if (item) {
      setEditAlbaData(item);
      setEditAlbaModalVisible(true);
    }
  };

  // 수정 저장
  const handleSaveEditAlba = () => {
    if (!editAlbaData) return;

    const updated = albaList.map(item =>
      item.App_IDX === editAlbaData.App_IDX ? editAlbaData : item
    );
    setAlbaList(updated);
    setEditAlbaModalVisible(false);
    setEditAlbaData(null);
    setSelectedAlbaKeys([]);
    message.success('수정되었습니다.');
  };

  // 일용직 등록 (저장)
  const handleSaveAlba = async () => {
    if (!jobName || !startDate || !endDate || !albaName || !albaJumin || !albaCell || !albaCost) {
      message.warning('모든 항목을 입력해주세요.');
      return;
    }

    // 주민번호 13자리 체크 및 유효성 검증
    const juminNumbers = albaJumin.replace(/[^0-9]/g, '');
    if (juminNumbers.length !== 13) {
      setAlbaJuminError('주민등록번호는 13자리여야 합니다.');
      messageApi.error('주민등록번호는 13자리여야 합니다.');
      return;
    }

    const validation = validateKoreanID(juminNumbers);
    if (!validation.valid) {
      setAlbaJuminError(validation.reason || '잘못된 주민번호');
      messageApi.error(`주민번호 오류: ${validation.reason}`);
      return;
    }

    // 전화번호 자리수 체크
    const cellNumbers = albaCell.replace(/[^0-9]/g, '');
    if (cellNumbers.length < 9 || cellNumbers.length > 11) {
      setAlbaCellError('전화번호는 9-11자리여야 합니다.');
      messageApi.error('전화번호를 확인해주세요.');
      return;
    }

    try {
      // tblAlba + tblApp 저장
      const result = await albaService.insertAlba({
        Job_Name: jobName,
        S_Date: startDate,
        E_Date: endDate,
        Alba_Name: albaName,
        Alba_Jumin: juminNumbers,
        Alba_Cell: cellNumbers,
        Alba_Cost: parseInt(albaCost)
      });

      if (result.resultCode === 200) {
        // 현재 세션 목록에 추가
        const newItem = {
          App_IDX: Date.now(), // 임시 ID
          Job_Name: jobName,
          S_Date: startDate,
          E_Date: endDate,
          Alba_Name: albaName,
          Alba_Jumin: juminNumbers,
          Alba_Cell: cellNumbers,
          Alba_Cost: parseInt(albaCost)
        };
        setAlbaList([newItem, ...albaList]);
        message.success('일용직이 등록되었습니다.');
        handleResetForm();
      } else {
        message.error(result.message || '등록 실패');
      }
    } catch (error) {
      console.error('저장 오류:', error);
      message.error('저장 중 오류가 발생했습니다.');
    }
  };

  // 폼 초기화
  const handleResetForm = () => {
    modal.confirm({
      title: '초기화',
      content: '입력 내용과 등록된 목록을 모두 초기화하시겠습니까?',
      okText: '초기화',
      okType: 'danger',
      cancelText: '취소',
      onOk: () => {
        setJobName('');
        setStartDate('');
        setEndDate('');
        setAlbaName('');
        setAlbaJumin('');
        setAlbaCell('');
        setAlbaCost('');
        setAlbaList([]);
        setSelectedAlbaKeys([]);
        message.info('입력 내용 및 등록된 목록이 초기화되었습니다.');
      },
    });
  };

  // 인력풀 조회
  const fetchPoolList = async () => {
    try {
      const response = await albaService.getPoolList();
      if (response.resultCode === 200) {
        setPoolList(response.data);
        setPoolModalVisible(true);
      } else {
        message.error(response.message || '인력풀 조회 실패');
      }
    } catch (error) {
      console.error('인력풀 조회 오류:', error);
      message.error('인력풀을 불러오는 중 오류가 발생했습니다.');
    }
  };

  // 인력풀 저장 (등록/수정)
  const handleSavePool = async () => {
    if (!poolFormName || !poolFormJumin || !poolFormCell) {
      message.warning('성명, 주민번호, 연락처를 모두 입력해주세요.');
      return;
    }

    // 주민번호 13자리 체크 및 유효성 검증
    const juminNumbers = poolFormJumin.replace(/[^0-9]/g, '');
    if (juminNumbers.length !== 13) {
      setPoolJuminError('주민등록번호는 13자리여야 합니다.');
      messageApi.error('주민등록번호는 13자리여야 합니다.');
      return;
    }

    const validation = validateKoreanID(juminNumbers);
    if (!validation.valid) {
      setPoolJuminError(validation.reason || '잘못된 주민번호');
      messageApi.error(`주민번호 오류: ${validation.reason}`);
      return;
    }

    // 전화번호 자리수 체크
    const cellNumbers = poolFormCell.replace(/[^0-9]/g, '');
    if (cellNumbers.length < 9 || cellNumbers.length > 11) {
      setPoolCellError('전화번호는 9-11자리여야 합니다.');
      messageApi.error('전화번호를 확인해주세요.');
      return;
    }

    try {
      const result = await albaService.insertPool({
        Alba_Name: poolFormName,
        Alba_Jumin: juminNumbers,  // 숫자만 전송
        Alba_Cell: poolFormCell.replace(/[^0-9]/g, ''),  // 숫자만 전송
      });

      if (result.resultCode === 200) {
        message.success('저장되었습니다.');
        setPoolFormName('');
        setPoolFormJumin('');
        setPoolFormCell('');
        fetchPoolList();
      } else if (result.resultCode === 201) {
        message.warning(result.message || '이미 등록된 주민번호입니다.');
      } else {
        message.error(result.message || '저장 실패');
      }
    } catch (error) {
      console.error('저장 오류:', error);
      message.error('저장 중 오류가 발생했습니다.');
    }
  };

  // 인력풀 삭제
  const handleDeletePool = (Alba_IDX: number, Alba_Name: string) => {
    modal.confirm({
      title: '인력풀 삭제',
      content: `${Alba_Name}님을 삭제하시겠습니까?`,
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          const result = await albaService.deletePool(Alba_IDX);
          if (result.resultCode === 200) {
            message.success('삭제되었습니다.');
            fetchPoolList(); // 목록 새로고침
          } else {
            message.error(result.message || '삭제 실패');
          }
        } catch (error) {
          console.error('삭제 오류:', error);
          message.error('삭제 중 오류가 발생했습니다.');
        }
      },
    });
  };

  // 인력풀 검색
  const handleSearchPool = () => {
    if (!poolSearchKeyword.trim()) {
      fetchPoolList(); // 전체 조회
      return;
    }

    const filtered = poolList.filter(item =>
      item.Alba_Name.includes(poolSearchKeyword.trim())
    );
    setPoolList(filtered);

    if (filtered.length === 0) {
      message.info('검색 결과가 없습니다.');
    }
  };

  // 일용직 검색 모달 열기 (현재 달 설정)
  const openAlbaSearchModal = () => {
    const today = dayjs();
    setSearchStartDate(today.startOf('month').format('YYYY-MM-DD'));
    setSearchEndDate(today.endOf('month').format('YYYY-MM-DD'));
    setSearchDateEnabled(true);
    setAlbaSearchModalVisible(true);
  };

  // 업무명 목록 조회 (기간 기반)
  const fetchJobNames = async () => {
    if (!searchStartDate || !searchEndDate) {
      message.warning('기간을 선택해주세요.');
      return;
    }

    try {
      const response = await api.get(`/alba/jobs?startDate=${searchStartDate}&endDate=${searchEndDate}`);
      if (response.data && response.data.length > 0) {
        const jobNames = response.data.map((item: any) => item.Job_Name);
        setJobNameList(jobNames);
      } else {
        setJobNameList([]);
        message.info('해당 기간의 업무명이 없습니다.');
      }
    } catch (error) {
      console.error('업무명 조회 오류:', error);
    }
  };

  // 일용직 검색 실행
  const handleSearchAlba = async () => {
    try {
      const params: any = {};
      if (searchDateEnabled && searchStartDate && searchEndDate) {
        params.chkDate = 'true';
        params.startDate = searchStartDate;
        params.endDate = searchEndDate;
      }
      if (searchJobName) params.jobName = searchJobName;
      if (searchAlbaName) params.searchName = searchAlbaName;

      const response = await albaService.getAlbaList(params);
      if (response.resultCode === 200) {
        setAlbaList(response.data);
        setAlbaSearchModalVisible(false);
        message.success(`${response.data.length}건이 조회되었습니다.`);
      }
    } catch (error) {
      console.error('검색 오류:', error);
      message.error('검색 중 오류가 발생했습니다.');
    }
  };

  // 이름 검색
  const handleSearchByName = async () => {
    if (!searchName.trim()) {
      message.warning('검색할 이름을 입력해주세요.');
      return;
    }

    try {
      const response = await albaService.searchByName(searchName.trim());
      if (response.resultCode === 200) {
        setSearchResults(response.data);
        if (response.data.length === 0) {
          message.info('검색 결과가 없습니다.');
        }
      } else {
        message.error(response.message || '검색 실패');
      }
    } catch (error) {
      console.error('검색 오류:', error);
      message.error('검색 중 오류가 발생했습니다.');
    }
  };

  // 검색 결과 선택 시 폼에 자동 입력
  const handleSelectSearchResult = (item: any) => {
    setAlbaName(item.Alba_Name);
    setAlbaJumin(item.Alba_Jumin);
    setAlbaCell(item.Alba_Cell);
    setSearchModalVisible(false);
    setSearchName('');
    setSearchResults([]);
    message.success(`${item.Alba_Name}님의 정보가 입력되었습니다.`);
  };

  // Excel 파일 선택
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFile(file);

    // Excel 파일 읽기
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = event.target?.result;
        const workbook = (window as any).XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = (window as any).XLSX.utils.sheet_to_json(worksheet);
        setUploadData(jsonData);
        console.log('📋 Excel 데이터:', jsonData);
      } catch (error) {
        console.error('Excel 읽기 오류:', error);
        message.error('Excel 파일을 읽는 중 오류가 발생했습니다.');
      }
    };
    reader.readAsBinaryString(file);
  };

  // Excel 업로드
  const handleUploadExcel = async () => {
    if (!uploadData || uploadData.length === 0) {
      message.warning('업로드할 파일을 선택해주세요.');
      return;
    }

    try {
      const result = await albaService.uploadExcel(uploadData);
      if (result.resultCode === 200) {
        message.success(result.message || '업로드 완료');
        setUploadModalVisible(false);
        setUploadFile(null);
        setUploadData([]);
        fetchPoolList(); // 인력풀 새로고침
      } else {
        message.error(result.message || '업로드 실패');
      }
    } catch (error) {
      console.error('업로드 오류:', error);
      message.error('업로드 중 오류가 발생했습니다.');
    }
  };

  // 파일 생성 (근로내역확인신고서)
  const handleGenerateReport = async () => {
    try {
      const blob = await albaService.generateWorkReport(selectedYear, selectedMonth);

      // Blob이 JSON 에러 응답인지 확인
      if (blob.type === 'application/json') {
        const text = await blob.text();
        const errorData = JSON.parse(text);
        message.error(errorData.message || '파일 생성 실패');
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `근로내역확인신고서_${selectedYear}년_${selectedMonth}월.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('파일이 다운로드되었습니다.');
      setReportModalVisible(false);
    } catch (error: any) {
      console.error('파일 생성 오류:', error);

      // Axios 에러에서 메시지 추출
      if (error.response?.data instanceof Blob) {
        const blob = error.response.data;
        if (blob.type === 'application/json') {
          const text = await blob.text();
          const errorData = JSON.parse(text);
          message.error(errorData.message || '파일 생성 실패');
          return;
        }
      }

      message.error('파일 생성 중 오류가 발생했습니다.');
    }
  };

  // 통계 생성
  const handleGenerateStatistics = async () => {
    if (!statsStartDate || !statsEndDate) {
      message.warning('시작일과 종료일을 선택해주세요.');
      return;
    }

    try {
      const blob = await albaService.generateStatistics(statsStartDate, statsEndDate);

      // Blob이 JSON 에러 응답인지 확인
      if (blob.type === 'application/json') {
        const text = await blob.text();
        const errorData = JSON.parse(text);
        message.error(errorData.message || '통계 생성 실패');
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `일용직_월별_통계_${statsStartDate}_${statsEndDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('통계 파일이 다운로드되었습니다.');
      setStatsModalVisible(false);
    } catch (error: any) {
      console.error('통계 생성 오류:', error);

      // Axios 에러에서 메시지 추출
      if (error.response?.data instanceof Blob) {
        const blob = error.response.data;
        if (blob.type === 'application/json') {
          const text = await blob.text();
          const errorData = JSON.parse(text);
          message.error(errorData.message || '통계 생성 실패');
          return;
        }
      }

      message.error('통계 생성 중 오류가 발생했습니다.');
    }
  };

  // 온라인 모집공고 조회
  const fetchOnlineExamList = async () => {
    setLoading(true);
    try {
      const response = await albaService.getOnlineExamList();
      if (response.resultCode === 200) {
        setOnlineExamList(response.data);
      }
    } catch (error) {
      console.error('온라인 모집공고 조회 오류:', error);
      message.error('온라인 모집공고를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 지원자 조회
  const fetchApplicants = async () => {
    if (selectedExams.length === 0) {
      message.warning('모집공고를 선택해주세요.');
      return;
    }

    setLoading(true);
    try {
      const response = await albaService.getOnlineApplicantList(selectedExams, companyName);
      if (response.resultCode === 200) {
        setApplicants(response.data);
        setApplicantModalVisible(true);
        setModalVisible(false);
      }
    } catch (error) {
      console.error('지원자 조회 오류:', error);
      message.error('지원자 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 지원자 엑셀 다운로드
  const handleDownloadExcel = async () => {
    if (selectedExams.length === 0) {
      message.warning('모집공고를 선택해주세요.');
      return;
    }

    setLoading(true);
    try {
      const response = await albaService.getOnlineApplicantList(selectedExams, companyName);
      if (response.resultCode === 200 && response.data.length > 0) {
        const applicantsData = response.data.map((item) => {
          const albaType = item.Alba_Type === 1 ? '감독관' : item.Alba_Type === 2 ? '관리요원' : '방송요원';
          return {
            jobName: `${item.Place_Name} ${albaType}`,
            eduDate: item.Edu_Date ? dayjs(item.Edu_Date).format('YYYY-MM-DD') : '-',
            examDate: item.Exam_Date ? dayjs(item.Exam_Date).format('YYYY-MM-DD') : '-',
            name: item.U_Name || '',
            jumin: item.Alba_Jumin || '',
            cell: item.U_Cell || '',
            cost: item.Cost || 0,
          };
        });

        const blob = await albaService.downloadApplicantsExcel(applicantsData);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const placeName = applicantsData[0].jobName.split(' ')[0] || '지원자';
        const examDate = applicantsData[0].examDate.replace(/-/g, '');
        link.download = `${placeName}_지원자리스트_${examDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        message.success('엑셀 파일이 다운로드되었습니다.');
        setModalVisible(false);
      } else {
        message.warning('조회된 지원자가 없습니다.');
      }
    } catch (error) {
      console.error('엑셀 다운로드 오류:', error);
      message.error('엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 온라인 모집공고 테이블 컬럼
  const examColumns: ColumnsType<AlbaExam> = [
    {
      title: <Checkbox onChange={(e) => {
        if (e.target.checked) {
          setSelectedExams(onlineExamList.map(item => item.Exam_IDX));
        } else {
          setSelectedExams([]);
        }
      }} />,
      width: 50,
      render: (_, record) => (
        <Checkbox
          checked={selectedExams.includes(record.Exam_IDX)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedExams([...selectedExams, record.Exam_IDX]);
            } else {
              setSelectedExams(selectedExams.filter(id => id !== record.Exam_IDX));
            }
          }}
        />
      ),
    },
    {
      title: 'No',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: '구분',
      dataIndex: 'Alba_Type',
      width: 100,
      render: (value) => {
        const types = { 1: '감독관', 2: '관리요원', 3: '방송요원' };
        return types[value as keyof typeof types] || '-';
      },
    },
    {
      title: '지역',
      dataIndex: 'Place_Area',
      width: 100,
    },
    {
      title: '장소',
      dataIndex: 'Place_Name',
    },
    {
      title: '교육일자',
      dataIndex: 'Edu_Date',
      width: 120,
      render: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
    {
      title: '감시일자',
      dataIndex: 'Exam_Date',
      width: 120,
      render: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
    {
      title: '지원자',
      dataIndex: 'APP_CNT',
      width: 80,
      align: 'center',
    },
    {
      title: '비용',
      dataIndex: 'Cost',
      width: 120,
      align: 'right',
      render: (value) => value ? value.toLocaleString() : '0',
    },
  ];

  // 선택된 지원자 엑셀 다운로드
  const handleDownloadSelectedApplicants = async () => {
    const selectedData = applicants.filter((_, index) => selectedApplicants.includes(index));

    if (selectedData.length === 0) {
      message.warning('다운로드할 지원자를 선택해주세요.');
      return;
    }

    setLoading(true);
    try {
      const applicantsData = selectedData.map((item) => {
        const albaType = item.Alba_Type === 1 ? '감독관' : item.Alba_Type === 2 ? '관리요원' : '방송요원';
        return {
          jobName: `${item.Place_Name} ${albaType}`,
          eduDate: item.Edu_Date ? dayjs(item.Edu_Date).format('YYYY-MM-DD') : '-',
          examDate: item.Exam_Date ? dayjs(item.Exam_Date).format('YYYY-MM-DD') : '-',
          name: item.U_Name || '',
          jumin: item.Alba_Jumin || '',
          cell: item.U_Cell || '',
          cost: item.Cost || 0,
        };
      });

      const blob = await albaService.downloadApplicantsExcel(applicantsData);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const placeName = applicantsData[0].jobName.split(' ')[0] || '지원자';
      const examDate = applicantsData[0].examDate.replace(/-/g, '');
      link.download = `${placeName}_지원자리스트_${examDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('선택한 지원자 엑셀 파일이 다운로드되었습니다.');
    } catch (error) {
      console.error('엑셀 다운로드 오류:', error);
      message.error('엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 지원자 테이블 컬럼
  const applicantColumns: ColumnsType<OnlineApplicant> = [
    {
      title: <Checkbox onChange={(e) => {
        if (e.target.checked) {
          setSelectedApplicants(applicants.map((_, index) => index));
        } else {
          setSelectedApplicants([]);
        }
      }} />,
      width: 50,
      render: (_, __, index) => (
        <Checkbox
          checked={selectedApplicants.includes(index)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedApplicants([...selectedApplicants, index]);
            } else {
              setSelectedApplicants(selectedApplicants.filter(i => i !== index));
            }
          }}
        />
      ),
    },
    {
      title: 'No',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: '업무명',
      width: 200,
      render: (_, record) => {
        const albaType = record.Alba_Type === 1 ? '감독관' : record.Alba_Type === 2 ? '관리요원' : '방송요원';
        return `${record.Place_Name} ${albaType}`;
      },
    },
    {
      title: '근무시작',
      dataIndex: 'Edu_Date',
      width: 120,
      render: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
    {
      title: '근무종료',
      dataIndex: 'Exam_Date',
      width: 120,
      render: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
    {
      title: '성명',
      dataIndex: 'U_Name',
      width: 100,
    },
    {
      title: '주민번호',
      dataIndex: 'Alba_Jumin',
      width: 130,
      render: (value) => value && value.length !== 13 ? (
        <span style={{ color: '#ff8000', fontWeight: 'bold' }}>{value}</span>
      ) : value,
    },
    {
      title: '연락처',
      dataIndex: 'U_Cell',
      width: 130,
    },
    {
      title: '금액',
      dataIndex: 'Cost',
      width: 120,
      align: 'right',
      render: (value) => value ? value.toLocaleString() : '0',
    },
  ];

  return (
    <MainLayout>
      {/* 알바 입력 폼 */}
      <Card title="일용직 정보 입력" style={{ marginBottom: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>업무명</label>
            <Input
              placeholder="업무명을 입력하세요"
              value={jobName}
              onChange={(e) => setJobName(e.target.value)}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>근무 기간</label>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ width: '50%' }}
              />
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ width: '50%' }}
              />
            </Space.Compact>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>성명</label>
            <Input
              placeholder="성명을 입력하세요"
              value={albaName}
              onChange={(e) => setAlbaName(e.target.value)}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>주민등록번호</label>
            <Input
              placeholder="주민등록번호 입력"
              maxLength={14}
              value={albaJumin}
              onChange={(e) => handleAlbaJuminChange(e.target.value)}
              status={albaJuminError ? 'error' : ''}
            />
            {albaJuminError && (
              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                {albaJuminError}
              </div>
            )}
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>연락처</label>
            <Input
              placeholder="연락처 입력"
              maxLength={13}
              value={albaCell}
              onChange={(e) => handleAlbaCellChange(e.target.value)}
              status={albaCellError ? 'error' : ''}
            />
            {albaCellError && (
              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                {albaCellError}
              </div>
            )}
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>지급 금액</label>
            <Input
              placeholder="지급 금액 입력"
              type="number"
              value={albaCost}
              onChange={(e) => setAlbaCost(e.target.value)}
            />
          </div>
        </div>
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Button type="primary" onClick={handleSaveAlba}>저장</Button>
        </div>
      </Card>

      {/* 상단 버튼 그룹 */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Button onClick={handleResetForm}>초기화</Button>
          <Button onClick={fetchPoolList}>인력풀</Button>
          <Button onClick={openAlbaSearchModal}>검색</Button>
          <Button onClick={() => setUploadModalVisible(true)}>업로드</Button>
          <Button
            type="primary"
            icon={<TeamOutlined />}
            onClick={() => {
              setModalVisible(true);
              fetchOnlineExamList();
            }}
          >
            온라인
          </Button>
        </Space>
        <Space>
          <Button type="primary" style={{ background: '#52c41a', borderColor: '#52c41a' }} onClick={() => setReportModalVisible(true)}>
            파일 생성
          </Button>
          <Button type="primary" style={{ background: '#13c2c2', borderColor: '#13c2c2' }} onClick={() => setStatsModalVisible(true)}>
            통계
          </Button>
        </Space>
      </div>

      {/* 등록된 일용직 목록 */}
      <Card
        title="등록된 일용직 목록"
        extra={
          <Space>
            <Button size="small" onClick={() => setSelectedAlbaKeys(albaList.map(a => a.App_IDX))}>
              전체 선택
            </Button>
            <Button size="small" type="primary" style={{ background: '#52c41a', borderColor: '#52c41a' }} onClick={handleSaveExcel}>
              엑셀 파일 저장
            </Button>
            <Button size="small" style={{ background: '#faad14', borderColor: '#faad14', color: 'white' }} onClick={handleEditSelectedAlba}>
              선택 수정
            </Button>
            <Button size="small" danger onClick={handleDeleteSelectedAlba}>
              선택 삭제
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={albaList}
          rowKey="App_IDX"
          size="small"
          pagination={{ pageSize: 10 }}
          rowSelection={{
            selectedRowKeys: selectedAlbaKeys,
            onChange: (selectedKeys) => setSelectedAlbaKeys(selectedKeys),
          }}
          columns={[
            {
              title: 'No',
              key: 'no',
              width: 60,
              align: 'center' as const,
              render: (_, __, index) => index + 1
            },
            { title: '업무명', dataIndex: 'Job_Name', width: 150 },
            { title: '시작일', dataIndex: 'S_Date', width: 120, align: 'center' as const },
            { title: '종료일', dataIndex: 'E_Date', width: 120, align: 'center' as const },
            { title: '성명', dataIndex: 'Alba_Name', width: 100 },
            {
              title: '주민번호',
              dataIndex: 'Alba_Jumin',
              width: 130,
              render: (value) => value ? formatJumin(value) : '-'
            },
            {
              title: '연락처',
              dataIndex: 'Alba_Cell',
              width: 130,
              render: (value) => value ? formatPhone(value) : '-'
            },
            {
              title: '금액',
              dataIndex: 'Alba_Cost',
              width: 120,
              align: 'right' as const,
              render: (value) => value ? value.toLocaleString() + '원' : '-'
            },
          ]}
        />
      </Card>

      {/* 온라인 모집공고 선택 모달 */}
      <Modal
        title="온라인 모집공고 선택"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedExams([]);
        }}
        width={1000}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            취소
          </Button>,
          <Button key="view" type="primary" onClick={fetchApplicants}>
            지원자 보기
          </Button>,
          <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownloadExcel}>
            지원자 다운로드
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="회사명을 입력하세요 (생략 가능)"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            style={{ width: 300 }}
          />
        </div>

        <Table
          columns={examColumns}
          dataSource={onlineExamList}
          rowKey="Exam_IDX"
          loading={loading}
          pagination={false}
          scroll={{ y: 400 }}
          size="small"
        />
      </Modal>

      {/* 지원자 목록 모달 */}
      <Modal
        title={`지원자 목록 (${applicants.length}명)`}
        open={applicantModalVisible}
        onCancel={() => {
          setApplicantModalVisible(false);
          setSelectedApplicants([]);
        }}
        width={1200}
        footer={[
          <Button
            key="select-all"
            onClick={() => {
              if (selectedApplicants.length === applicants.length) {
                setSelectedApplicants([]);
              } else {
                setSelectedApplicants(applicants.map((_, index) => index));
              }
            }}
          >
            {selectedApplicants.length === applicants.length ? '전체 해제' : '전체 선택'}
          </Button>,
          <Button
            key="download-excel"
            type="primary"
            style={{ background: '#52c41a', borderColor: '#52c41a' }}
            icon={<DownloadOutlined />}
            onClick={handleDownloadSelectedApplicants}
          >
            엑셀 파일 저장
          </Button>,
          <Button
            key="register"
            type="primary"
            style={{ background: '#faad14', borderColor: '#faad14' }}
            onClick={() => {
              if (selectedApplicants.length === 0) {
                message.warning('등록할 지원자를 선택해주세요.');
                return;
              }
              message.info(`${selectedApplicants.length}명의 지원자 등록 기능 (구현 예정)`);
            }}
          >
            선택 등록
          </Button>,
          <Button key="close" onClick={() => {
            setApplicantModalVisible(false);
            setSelectedApplicants([]);
          }}>
            닫기
          </Button>,
        ]}
      >
        <Table
          columns={applicantColumns}
          dataSource={applicants}
          rowKey={(record, index) => `${record.Exam_IDX}-${index}`}
          pagination={{
            pageSize: 10,
            showTotal: (total) => `전체 ${total}명`,
          }}
          size="small"
        />
      </Modal>

      {/* 파일 생성 모달 */}
      <Modal
        title="근로내역확인신고서 생성"
        open={reportModalVisible}
        onOk={handleGenerateReport}
        onCancel={() => setReportModalVisible(false)}
        okText="파일 생성"
        cancelText="취소"
      >
        <Space style={{ width: '100%' }}>
          <div>
            <label>년도: </label>
            <Input
              type="number"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              style={{ width: 100 }}
            />
          </div>
          <div>
            <label>월: </label>
            <Input
              type="number"
              min={1}
              max={12}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              style={{ width: 80 }}
            />
          </div>
        </Space>
      </Modal>

      {/* 통계 생성 모달 */}
      <Modal
        title="일용직 월별 통계"
        open={statsModalVisible}
        onOk={handleGenerateStatistics}
        onCancel={() => setStatsModalVisible(false)}
        okText="통계 생성"
        cancelText="취소"
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* 빠른 선택 버튼 */}
          <div>
            <Space wrap>
              <Button onClick={() => {
                const today = dayjs();
                setStatsStartDate(today.format('YYYY-MM-DD'));
                setStatsEndDate(today.format('YYYY-MM-DD'));
              }}>
                오늘
              </Button>
              {[0, 1, 2, 3].map((monthsAgo) => {
                const targetMonth = dayjs().subtract(monthsAgo, 'month');
                const monthName = targetMonth.format('M월');
                
                return (
                  <Button
                    key={monthsAgo}
                    onClick={() => {
                      setStatsStartDate(targetMonth.startOf('month').format('YYYY-MM-DD'));
                      setStatsEndDate(targetMonth.endOf('month').format('YYYY-MM-DD'));
                    }}
                  >
                    {monthName}
                  </Button>
                );
              })}
              <Button onClick={() => {
                setStatsStartDate('');
                setStatsEndDate('');
              }}>
                초기화
              </Button>
            </Space>
          </div>

          {/* 날짜 직접 입력 */}
          <div>
            <label style={{ fontWeight: 'bold', marginBottom: 8, display: 'block' }}>근무일자</label>
            <Space>
              <Input
                type="date"
                value={statsStartDate}
                onChange={(e) => setStatsStartDate(e.target.value)}
                style={{ width: 150 }}
              />
              <span>~</span>
              <Input
                type="date"
                value={statsEndDate}
                onChange={(e) => setStatsEndDate(e.target.value)}
                style={{ width: 150 }}
              />
            </Space>
          </div>
        </Space>
      </Modal>

      {/* 일용직 검색 모달 */}
      <Modal
        title="일용직 등록내역 검색"
        open={albaSearchModalVisible}
        onOk={handleSearchAlba}
        onCancel={() => setAlbaSearchModalVisible(false)}
        okText="검색"
        cancelText="취소"
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* 빠른 선택 버튼 */}
          <div>
            <Space wrap>
              <Button onClick={() => {
                const today = dayjs();
                setSearchStartDate(today.format('YYYY-MM-DD'));
                setSearchEndDate(today.format('YYYY-MM-DD'));
              }}>
                오늘
              </Button>
              {[0, 1, 2].map((monthsAgo) => {
                const targetMonth = dayjs().subtract(monthsAgo, 'month');
                const monthName = targetMonth.format('M월');
                return (
                  <Button
                    key={monthsAgo}
                    onClick={() => {
                      setSearchStartDate(targetMonth.startOf('month').format('YYYY-MM-DD'));
                      setSearchEndDate(targetMonth.endOf('month').format('YYYY-MM-DD'));
                    }}
                  >
                    {monthName}
                  </Button>
                );
              })}
            </Space>
          </div>

          {/* 검색일자 */}
          <div>
            <Checkbox
              checked={searchDateEnabled}
              onChange={(e) => setSearchDateEnabled(e.target.checked)}
            >
              기간 검색
            </Checkbox>
            <Space style={{ marginTop: 8 }}>
              <Input
                type="date"
                value={searchStartDate}
                onChange={(e) => setSearchStartDate(e.target.value)}
                disabled={!searchDateEnabled}
                style={{ width: 150 }}
              />
              <span>~</span>
              <Input
                type="date"
                value={searchEndDate}
                onChange={(e) => setSearchEndDate(e.target.value)}
                disabled={!searchDateEnabled}
                style={{ width: 150 }}
              />
            </Space>
          </div>

          {/* 업무명 */}
          <div>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                addonBefore="업무명"
                value={searchJobName}
                onChange={(e) => setSearchJobName(e.target.value)}
                placeholder="업무명 입력"
              />
              <Button onClick={fetchJobNames} disabled={!searchDateEnabled}>
                업무명 조회
              </Button>
            </Space.Compact>
            {jobNameList.length > 0 && (
              <Select
                style={{ width: '100%', marginTop: 8 }}
                placeholder="조회된 업무명 선택"
                value={searchJobName}
                onChange={setSearchJobName}
                options={jobNameList.map(name => ({ label: name, value: name }))}
              />
            )}
          </div>

          {/* 성명 */}
          <div>
            <Input
              addonBefore="성명"
              value={searchAlbaName}
              onChange={(e) => setSearchAlbaName(e.target.value)}
              placeholder="성명 입력"
            />
          </div>
        </Space>
      </Modal>

      {/* 이름 검색 모달 (인력풀) */}
      <Modal
        title="인력풀 검색"
        open={searchModalVisible}
        onCancel={() => {
          setSearchModalVisible(false);
          setSearchName('');
          setSearchResults([]);
        }}
        footer={null}
        width={600}
      >
        <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
          <Input
            placeholder="이름을 입력하세요"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onPressEnter={handleSearchByName}
          />
          <Button type="primary" onClick={handleSearchByName}>검색</Button>
        </Space.Compact>

        {searchResults.length > 0 && (
          <Table
            dataSource={searchResults}
            rowKey={(record, index) => `search-${index}`}
            size="small"
            pagination={false}
            onRow={(record) => ({
              onClick: () => handleSelectSearchResult(record),
              style: { cursor: 'pointer' }
            })}
            columns={[
              { title: '성명', dataIndex: 'Alba_Name', width: 120 },
              { title: '주민등록번호', dataIndex: 'Alba_Jumin', width: 150 },
              { title: '연락처', dataIndex: 'Alba_Cell', width: 130 },
            ]}
          />
        )}
      </Modal>

      {/* Excel 업로드 모달 */}
      <Modal
        title="인력풀 Excel 업로드"
        open={uploadModalVisible}
        onOk={handleUploadExcel}
        onCancel={() => {
          setUploadModalVisible(false);
          setUploadFile(null);
          setUploadData([]);
        }}
        okText="업로드"
        cancelText="취소"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* 템플릿 다운로드 */}
          <div>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => window.open('/public/sample/인력풀 리스트.xlsx', '_blank')}
            >
              템플릿 다운로드
            </Button>
            <span style={{ marginLeft: 8, fontSize: '12px', color: '#999' }}>
              (Excel 파일 형식: 성명, 주민번호, 연락처)
            </span>
          </div>

          <div>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              style={{ marginBottom: 8 }}
            />
          </div>

          {uploadData.length > 0 && (
            <div>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                미리보기 ({uploadData.length}건)
              </div>
              <Table
                dataSource={uploadData.slice(0, 5)}
                rowKey={(record, index) => `upload-${index}`}
                size="small"
                pagination={false}
                columns={[
                  { title: '성명', dataIndex: '성명', width: 100 },
                  { title: '주민번호', dataIndex: '주민번호', width: 130 },
                  { title: '연락처', dataIndex: '연락처', width: 120 },
                ]}
              />
              {uploadData.length > 5 && (
                <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
                  ... 외 {uploadData.length - 5}건
                </div>
              )}
            </div>
          )}
        </Space>
      </Modal>

      {/* 인력풀 관리 모달 */}
      <Modal
        title="인력풀 관리"
        open={poolModalVisible}
        onCancel={() => setPoolModalVisible(false)}
        footer={null}
        width={900}
      >
        {/* 입력 폼 */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ padding: '8px', fontWeight: 'bold', width: '80px', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                  성명
                </td>
                <td style={{ padding: '8px', border: '1px solid #d9d9d9' }}>
                  <Input
                    placeholder="성명 입력"
                    value={poolFormName}
                    onChange={(e) => setPoolFormName(e.target.value)}
                  />
                </td>
                <td style={{ padding: '8px', fontWeight: 'bold', width: '100px', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                  주민번호
                </td>
                <td style={{ padding: '8px', border: '1px solid #d9d9d9' }}>
                  <Input
                    placeholder="13자리"
                    maxLength={14}
                    value={poolFormJumin}
                    onChange={(e) => handlePoolJuminChange(e.target.value)}
                    status={poolJuminError ? 'error' : ''}
                  />
                  {poolJuminError && (
                    <div style={{ color: '#ff4d4f', fontSize: '11px', marginTop: '2px' }}>
                      {poolJuminError}
                    </div>
                  )}
                </td>
                <td style={{ padding: '8px', fontWeight: 'bold', width: '80px', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                  연락처
                </td>
                <td style={{ padding: '8px', border: '1px solid #d9d9d9' }}>
                  <Input
                    placeholder="연락처 입력"
                    maxLength={13}
                    value={poolFormCell}
                    onChange={(e) => handlePoolCellChange(e.target.value)}
                    status={poolCellError ? 'error' : ''}
                  />
                  {poolCellError && (
                    <div style={{ color: '#ff4d4f', fontSize: '11px', marginTop: '2px' }}>
                      {poolCellError}
                    </div>
                  )}
                </td>
                <td style={{ padding: '8px', width: '80px', border: '1px solid #d9d9d9', textAlign: 'center' }}>
                  <Button type="primary" onClick={handleSavePool} block>
                    저장
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </Card>

        {/* 검색 */}
        <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
          <Input
            placeholder="이름으로 검색"
            value={poolSearchKeyword}
            onChange={(e) => setPoolSearchKeyword(e.target.value)}
            onPressEnter={handleSearchPool}
          />
          <Button onClick={handleSearchPool}>검색</Button>
          <Button onClick={() => {
            setPoolSearchKeyword('');
            fetchPoolList();
          }}>전체</Button>
        </Space.Compact>

        <Table
          dataSource={poolList}
          rowKey="Alba_IDX"
          size="small"
          pagination={{ pageSize: 10 }}
          columns={[
            {
              title: 'No',
              dataIndex: 'Alba_IDX',
              width: 80,
              align: 'center' as const,
            },
            {
              title: '성명',
              dataIndex: 'Alba_Name',
              width: 120,
            },
            {
              title: '주민등록번호',
              dataIndex: 'Alba_Jumin',
              width: 150,
              render: (value) => value ? formatJumin(value) : '-'
            },
            {
              title: '연락처',
              dataIndex: 'Alba_Cell',
              width: 130,
              render: (value) => value ? formatPhone(value) : '-'
            },
            {
              title: '등록일',
              dataIndex: 'Reg_Date',
              width: 120,
              align: 'center' as const,
            },
            {
              title: '삭제',
              key: 'action',
              width: 80,
              align: 'center' as const,
              render: (_, record) => (
                <Button
                  danger
                  size="small"
                  onClick={() => handleDeletePool(record.Alba_IDX, record.Alba_Name)}
                >
                  삭제
                </Button>
              ),
            },
          ]}
        />
      </Modal>

      {/* 일용직 수정 모달 */}
      <Modal
        title="일용직 수정"
        open={editAlbaModalVisible}
        onOk={handleSaveEditAlba}
        onCancel={() => {
          setEditAlbaModalVisible(false);
          setEditAlbaData(null);
        }}
        okText="수정"
        cancelText="취소"
        width={600}
      >
        {editAlbaData && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ padding: '8px', fontWeight: 'bold', width: '100px', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                  업무명
                </td>
                <td style={{ padding: '8px', border: '1px solid #d9d9d9' }} colSpan={3}>
                  <Input
                    value={editAlbaData.Job_Name}
                    onChange={(e) => setEditAlbaData({ ...editAlbaData, Job_Name: e.target.value })}
                  />
                </td>
              </tr>
              <tr>
                <td style={{ padding: '8px', fontWeight: 'bold', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                  근무기간
                </td>
                <td style={{ padding: '8px', border: '1px solid #d9d9d9' }} colSpan={3}>
                  <Space>
                    <Input
                      type="date"
                      value={editAlbaData.S_Date}
                      onChange={(e) => setEditAlbaData({ ...editAlbaData, S_Date: e.target.value })}
                      style={{ width: 150 }}
                    />
                    <span>~</span>
                    <Input
                      type="date"
                      value={editAlbaData.E_Date}
                      onChange={(e) => setEditAlbaData({ ...editAlbaData, E_Date: e.target.value })}
                      style={{ width: 150 }}
                    />
                  </Space>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '8px', fontWeight: 'bold', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                  성명
                </td>
                <td style={{ padding: '8px', border: '1px solid #d9d9d9' }}>
                  <Input
                    value={editAlbaData.Alba_Name}
                    onChange={(e) => setEditAlbaData({ ...editAlbaData, Alba_Name: e.target.value })}
                  />
                </td>
                <td style={{ padding: '8px', fontWeight: 'bold', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                  주민번호
                </td>
                <td style={{ padding: '8px', border: '1px solid #d9d9d9' }}>
                  <Input
                    value={formatJumin(editAlbaData.Alba_Jumin)}
                    onChange={(e) => setEditAlbaData({ ...editAlbaData, Alba_Jumin: e.target.value.replace(/[^0-9]/g, '') })}
                    maxLength={14}
                  />
                </td>
              </tr>
              <tr>
                <td style={{ padding: '8px', fontWeight: 'bold', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                  연락처
                </td>
                <td style={{ padding: '8px', border: '1px solid #d9d9d9' }}>
                  <Input
                    value={formatPhone(editAlbaData.Alba_Cell)}
                    onChange={(e) => setEditAlbaData({ ...editAlbaData, Alba_Cell: e.target.value.replace(/[^0-9]/g, '') })}
                    maxLength={13}
                  />
                </td>
                <td style={{ padding: '8px', fontWeight: 'bold', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                  금액
                </td>
                <td style={{ padding: '8px', border: '1px solid #d9d9d9' }}>
                  <Input
                    type="number"
                    value={editAlbaData.Alba_Cost}
                    onChange={(e) => setEditAlbaData({ ...editAlbaData, Alba_Cost: parseInt(e.target.value) })}
                    addonAfter="원"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </Modal>
    </MainLayout>
  );
};

export default AlbaList;
